import { SafetyOutlined } from "@ant-design/icons";
import { useLocation, useParams } from "@tanstack/react-router";
import { Alert, Skeleton, Typography } from "antd";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { RoleEditor } from "../components/RoleEditor";
import { RolesListPane } from "../components/RolesListPane";
import { useRole } from "../hooks/useRoles";

const { Title } = Typography;

interface EditorHandlers {
	onDirtyChange: (dirty: boolean) => void;
}

function RoleEditorLoader({ roleId, onDirtyChange }: EditorHandlers & { roleId: string }) {
	const { t } = useTranslation("roles");
	const { data: role, isLoading } = useRole(roleId);

	if (isLoading) {
		return (
			<div className="p-6">
				<Skeleton active />
			</div>
		);
	}

	// ⚠️ URL to'g'ridan-to'g'ri ochilganda (yaramas ID, xatcho'p) `role`
	// `undefined` bo'lib qoladi — bo'sh forma o'rniga aniq xabar.
	if (!role) {
		return (
			<div className="p-6">
				<Alert type="error" showIcon title={t("form.notFound")} />
			</div>
		);
	}

	return <RoleEditor role={role} onDirtyChange={onDirtyChange} />;
}

/**
 * Rollar — bitta ekran (spec `2026-09-14-rollar-bitta-ekran-design.md`):
 * chapda ro'yxat, o'ngda tahrirlash paneli. Uch marshrut
 * (`/roles`, `/roles/new`, `/roles/$roleId`) shu BITTA komponentni ko'rsatadi —
 * rejim URL'dan olinadi.
 *
 * ⚠️ `useParams({ strict: false })` — sahifa `routeTree.tsx`ni (poydevor
 * fayli) IMPORT QILMAYDI. `/roles/new` ni `useLocation` dan aniqlaymiz:
 * `/roles` va `/roles/new` ikkalasida ham `roleId` yo'q.
 */
export function RolesPage() {
	const { t } = useTranslation("roles");
	const params = useParams({ strict: false }) as { roleId?: string };
	const pathname = useLocation({ select: (location) => location.pathname });
	const isNew = /\/roles\/new\/?$/.test(pathname);
	const roleId = isNew ? undefined : params.roleId;

	const [dirty, setDirty] = useState(false);
	const [draftName, setDraftName] = useState("");
	const handleDirtyChange = useCallback((value: boolean) => setDirty(value), []);
	const handleDraftNameChange = useCallback((value: string) => setDraftName(value), []);

	const dirtyKey = dirty ? (isNew ? "new" : (roleId ?? null)) : null;

	return (
		<div>
			<div className="mb-6">
				<Title level={3} className="!mb-0">
					{t("list.title")}
				</Title>
				<span className="text-sm text-gray-400">{t("list.subtitle")}</span>
			</div>

			<div className="grid grid-cols-1 overflow-hidden rounded-[10px] border border-[#e5e6e3] lg:grid-cols-[296px_minmax(0,1fr)] dark:border-[#2a2e33]">
				<RolesListPane
					selectedId={roleId}
					isNew={isNew}
					dirtyKey={dirtyKey}
					draftName={draftName}
				/>

				<div className="min-w-0 bg-white dark:bg-[#181b1f]">
					{isNew ? (
						<RoleEditor
							key="new"
							onDirtyChange={handleDirtyChange}
							onDraftNameChange={handleDraftNameChange}
						/>
					) : roleId ? (
						// ⚠️ `key` MAJBURIY — `RoleEditor` izohiga qarang.
						<RoleEditorLoader key={roleId} roleId={roleId} onDirtyChange={handleDirtyChange} />
					) : (
						<div className="flex flex-col items-center justify-center gap-2 px-6 py-24 text-[#8a929e] dark:text-[#6b7280]">
							<SafetyOutlined className="text-2xl" />
							<span>{t("page.emptySelection")}</span>
						</div>
					)}
				</div>
			</div>
		</div>
	);
}
